import { useMutation } from "@apollo/client";
import Note3 from "components/Note/Note3";
import { ComposeActions, ComposeSection } from "components/ui/v2";
import { useModal } from "context/modal/modal.provider";
import { gqlMessage } from "gql";
import { ICreateMessageInput } from "gql/Message/mutations";
import { useExperienceGain } from "hooks/useExperienceGain";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { EMessageType, IMessage } from "types/Message.type";
import { IUser } from "types/User.types";
import useSound from "use-sound";
import { checkIfNodeHaveText } from "utils/message";

interface IForm {
  message: string;
}
interface INewMessageBenefitProps {
  resetMessageInput: () => void;
  usersToMention: IUser[];
  inline?: boolean;
}

const NewMessageBenefit = (props: INewMessageBenefitProps) => {
  const { resetMessageInput, usersToMention } = props;
  const [playComplete] = useSound("/sounds/feedback-sound.mp3", { volume: 0.25 });
  const { actions: experienceActions } = useExperienceGain();
  const { closeModal } = useModal();
  const { control, handleSubmit, setValue, watch } = useForm<IForm>();
  const { t } = useTranslation("common");
  const [contentEmpty, setContentEmpty] = useState<boolean>(true);
  const messageValue = watch("message");

  const [createMessage, { loading: createMessageLoading }] = useMutation<
    { createMessage: IMessage },
    { createMessageInput: ICreateMessageInput }
  >(gqlMessage.mutations.CREATE_MESSAGE, {
    onCompleted: () => {
      setValue("message", "");
      resetMessageInput();
      toast.success(t("Mensaje publicado correctamente"));
      playComplete();
      experienceActions.sendMessage();
      closeModal();
    }
  });

  const handlePublish = (data: IForm) => {
    createMessage({
      variables: {
        createMessageInput: {
          type: EMessageType.BENEFIT,
          text: data?.message
        }
      }
    });
  };

  useEffect(() => {
    if (messageValue) {
      try {
        const blocks = JSON.parse(messageValue);
        const hasText = checkIfNodeHaveText(blocks);
        setContentEmpty(!hasText);
      } catch {
        setContentEmpty(true);
      }
    } else {
      setContentEmpty(true);
    }
  }, [messageValue]);

  return (
    <>
      <ComposeSection compact>
        <Controller
          name="message"
          control={control}
          render={({ field }) => (
            <Note3
              {...field}
              compact
              onChange={(e) => field.onChange(e)}
              placeholder={t("Describe el beneficio… @menciona o pega imágenes")}
              usersToMention={usersToMention}
            />
          )}
        />
      </ComposeSection>
      <ComposeActions
        compact
        onPublish={handleSubmit(handlePublish)}
        loading={createMessageLoading}
        disabled={contentEmpty}
        loadingText={t("Publicando")}
        publishText={t("Publicar")}
        publishVariant="violet"
      />
    </>
  );
};

export default NewMessageBenefit;
